import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import classNames from "classnames";
import {
  createColumnHelper,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  Row,
  useReactTable,
} from "react-table-8.10.7";
import Table from "components/DesignSystem/Table/V2/Table";
import { Button } from "components/DesignSystem/Button/Button";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { CheckoutModal } from "components/CheckoutModal/CheckoutModal";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { CalendarBlank } from "components/icons/CalendarBlank";
import { CaretDown } from "components/icons/CaretDown";
import { Cart } from "components/icons/Cart";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import Wind from "components/icons/Wind";
import { emptyApi } from "store/apis/emptyApi";
import { useAddProductsToCartAndCheckoutMutation } from "store/apis/billing";
import { BillingInvoice } from "types/Models/billing";
import { BackendError } from "types/utils/error";
import { useGetDetectedFilingsQuery } from "v2/store/taxSetupApi";
import { FilingActionsMenu } from "v2/components/FilingsDashboard/FilingActionsMenu";
import { PayFilingConfirmModal } from "v2/components/FilingsDashboard/PayFilingConfirmModal";
import { groupFilingsBySeason } from "v2/utils/mapDetectedFilings";
import { FilingAction, FilingNode } from "v2/types/filingsDashboard.types";
import { nestFilingsByGroup } from "v2/utils/nestFilingsByGroup";
import { isFilingFiledExternally } from "v2/utils/isFilingFiledExternally";
import { getFilingStatusTag } from "v2/utils/getFilingStatusTag";
import { CUSTOMER_PAYER, FCA_PAYER } from "constants/payer";
import { ConditionalLink } from "components/conditionalLink";
import { SmallPlusIcon } from "components/icons/export const SmallPlusIcon";

const DETECTED_FILINGS_TAG = "TAX_DETECTED_FILINGS";
const GENERIC_ERROR = "Something went wrong";

type DashboardEntityRef = { id: string; name: string };

const OVERDUE_LABEL = "Overdue";
const COLUMN_COUNT = 4;
const COLUMN_WIDTHS: Record<string, string> = {
  name: "100%",
};

const COLUMN_PADDINGS: Record<string, string> = {
  name: "!t-pl-5 !t-pr-3",
  date: "!t-pl-0 !t-pr-3",
  action: "!t-pl-0 !t-pr-3",
  menu: "!t-pl-0 !t-pr-5",
};

const CARET_ICON_SIZE = 12;
const CART_ICON_SIZE = 12;
const CONNECTOR_LEFT = "t-left-[52px]";

const TreeConnector = ({
  isFirst,
  isLast,
}: {
  isFirst?: boolean;
  isLast?: boolean;
}) => {
  const elbowHeightClassName = isFirst ? "t-h-[34px]" : "t-h-[46px]";

  return (
    <>
      {!isLast && (
        <span
          className={classNames(
            CONNECTOR_LEFT,
            "t-absolute t-bottom-0 t-top-0 t-w-px t-bg-neutral-10"
          )}
        />
      )}

      <span
        className={classNames(
          CONNECTOR_LEFT,
          elbowHeightClassName,
          "t-absolute t-top-0 t-w-3.5 t-rounded-bl-lg t-border-0 t-border-b t-border-l t-border-solid t-border-neutral-10"
        )}
      />
    </>
  );
};

const OverdueLabel = () => (
  <span className="t-ml-3 t-inline-flex t-items-center t-gap-1 t-whitespace-nowrap t-align-middle">
    <span className="t-h-1.5 t-w-1.5 t-rounded-full t-bg-red" />
    <span className="t-text-body-sm t-text-red-70">{OVERDUE_LABEL}</span>
  </span>
);

const ACTION_CELL_CLASS =
  "t-flex t-items-center t-justify-end t-whitespace-nowrap";

const ActionCell = ({
  action,
  status,
  onPay,
}: {
  action?: FilingAction;
  status?: string;
  onPay: () => void;
}) => {
  if (!action) {
    return null;
  }

  const statusTag = getFilingStatusTag(status);

  if (statusTag) {
    return (
      <div className={ACTION_CELL_CLASS}>
        <Tag tagType={statusTag.tagType} rounded size="small" icon>
          {statusTag.label}
        </Tag>
      </div>
    );
  }

  if (action.kind === "pay") {
    return (
      <div className={ACTION_CELL_CLASS}>
        <Button customType="secondary" size="xsmall" onClick={onPay}>
          {action.label}
        </Button>
      </div>
    );
  }

  return (
    <div className={classNames(ACTION_CELL_CLASS, "t-gap-1.5 t-text-text-30")}>
      <span className="t-flex t-shrink-0 t-p-0.5">
        <Cart width={CART_ICON_SIZE} height={CART_ICON_SIZE} />
      </span>
      <span className="t-text-body-sm">{action.label}</span>
    </div>
  );
};

const columnHelper = createColumnHelper<FilingNode>();

const buildColumns = (
  onPay: (filing: FilingNode) => void,
  entityId: string,
  season: string
) => [
  columnHelper.accessor("title", {
    id: "name",
    cell: ({ row }) => {
      const { title, description, isLast, isOverdue, status } = row.original;

      const isSubRow = row.depth > 0;
      const isFiledExternally = isFilingFiledExternally(status);
      const shouldShowOverdue = isOverdue && !isFiledExternally;

      const caretClassName = classNames(
        "t-flex t-transition-transform t-duration-300",
        { "-t-rotate-90": !row.getIsExpanded() }
      );

      const contentClassName = classNames("t-flex t-min-w-0 t-flex-col", {
        "t-pl-[52px]": isSubRow,
      });

      const handleCaretClick = (
        event: React.MouseEvent<HTMLButtonElement>
      ): void => {
        event.stopPropagation();
        row.toggleExpanded();
      };

      return (
        <div className="t-flex t-items-start t-gap-2">
          {row.getCanExpand() && (
            <span className="t-flex t-w-4 t-shrink-0 t-items-center t-justify-center t-py-[3px]">
              <Button
                customType="ghost_icon"
                size="small"
                className="!t-h-4 !t-min-h-0 !t-w-4 !t-px-0"
                onClick={handleCaretClick}
              >
                <span className={caretClassName}>
                  <CaretDown width={CARET_ICON_SIZE} height={CARET_ICON_SIZE} />
                </span>
              </Button>
            </span>
          )}

          {isSubRow && (
            <TreeConnector isFirst={row.index === 0} isLast={isLast} />
          )}

          <div className={contentClassName}>
            <span className="t-text-body t-text-text-60">
              {title}
              {shouldShowOverdue && <OverdueLabel />}
            </span>

            {description && (
              <span className="t-text-body-sm-italic t-text-neutral-40">
                {description}
              </span>
            )}
          </div>
        </div>
      );
    },
  }),

  columnHelper.accessor("dueDate", {
    id: "date",
    cell: ({ row }) => {
      const { dueDate, status } = row.original;

      const isFiledExternally = isFilingFiledExternally(status);

      if (!dueDate || isFiledExternally) {
        return null;
      }

      return (
        <div className="t-flex t-items-center t-gap-1.5 t-whitespace-nowrap">
          <span className="t-flex t-shrink-0 t-p-0.5 t-text-text-30">
            <CalendarBlank />
          </span>
          <span className="t-text-body-sm t-text-text-60">{dueDate}</span>
        </div>
      );
    },
  }),

  columnHelper.display({
    id: "action",
    cell: ({ row }) => (
      <ActionCell
        action={row.original.action}
        status={row.original.status}
        onPay={() => onPay(row.original)}
      />
    ),
  }),

  columnHelper.display({
    id: "menu",
    cell: ({ row }) => {
      if (row.original.isGroupRow) return null;

      if (isFilingFiledExternally(row.original.status)) return null;

      return (
        <div className="t-flex t-items-center t-justify-center">
          <FilingActionsMenu
            filing={row.original}
            entityId={entityId}
            season={season}
          />
        </div>
      );
    },
  }),
];

const getRowBorderClassName = (row: Row<FilingNode>) => {
  const isExpandedParent = row.getCanExpand() && row.getIsExpanded();
  const isNonLastSubRow = row.depth > 0 && !row.original.isLast;

  if (isExpandedParent || isNonLastSubRow) {
    return "t-border-b-0";
  }

  return "";
};

type FilingsSeasonTableProps = {
  season: string;
  filings: FilingNode[];
  entityId: string;
  onPay: (filing: FilingNode) => void;
};

const seasonHeading = (season: string): string =>
  /^\d{4}$/.test(season) ? `SEASON ${season}` : season.toUpperCase();

const FilingTableRow = ({ row }: { row: Row<FilingNode> }) => {
  const isSubRow = row.depth > 0;
  const isFirstSubRow = isSubRow && row.index === 0;
  const isCollapsedGroup = row.getCanExpand() && !row.getIsExpanded();
  const isBlockEnd =
    isCollapsedGroup || (isSubRow && Boolean(row.original.isLast));

  const rowClassName = classNames("t-align-top", {
    "!t-pt-0": isFirstSubRow,
    "!t-pt-[15px]": isSubRow && !isFirstSubRow,
    "!t-pt-4": !isSubRow,
    "!t-pb-[17px]": isBlockEnd,
    "!t-pb-[15px]": !isBlockEnd && !isSubRow,
    "!t-pb-0": !isBlockEnd && isSubRow,
  });

  const toggleExpanded = row.getCanExpand()
    ? row.getToggleExpandedHandler()
    : undefined;

  return (
    <Table.Row
      className={getRowBorderClassName(row)}
      onRowClick={toggleExpanded}
    >
      {row.getVisibleCells().map((cell) => (
        <Table.Cell
          key={cell.id}
          style={{ width: COLUMN_WIDTHS[cell.column.id] }}
          className={classNames(
            "t-relative",
            COLUMN_PADDINGS[cell.column.id],
            rowClassName
          )}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </Table.Cell>
      ))}
    </Table.Row>
  );
};

const FilingsSeasonTable = ({
  season,
  filings,
  entityId,
  onPay,
}: FilingsSeasonTableProps) => {
  const [expanded, setExpanded] = useState<ExpandedState>(true);

  const columns = useMemo(
    () => buildColumns(onPay, entityId, season),
    [onPay, entityId, season]
  );

  const groupedFilings = useMemo(
    () => nestFilingsByGroup(filings, season),
    [filings, season]
  );

  const table = useReactTable({
    data: groupedFilings,
    columns,
    state: { expanded },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  return (
    <Table.Content>
      <Table.Head>
        <tr>
          <Table.HeadCell
            colSpan={COLUMN_COUNT}
            isUpperCase={false}
            className="t-px-5 t-py-2 t-text-left !t-text-body-sm t-text-neutral-40"
          >
            {seasonHeading(season)}
          </Table.HeadCell>
        </tr>
      </Table.Head>

      <Table.Body>
        {table.getRowModel().rows.map((row) => (
          <FilingTableRow key={row.id} row={row} />
        ))}
      </Table.Body>
    </Table.Content>
  );
};

type EntityTabsProps = {
  entities: DashboardEntityRef[];
  countById: Record<string, number>;
  selectedId: string;
  onSelect: (id: string) => void;
};

type EntityTabProps = {
  entity: DashboardEntityRef;
  count: number;
  isActive: boolean;
  onSelect: (id: string) => void;
};

const TAB_CLASS =
  "t-shrink-0 t-whitespace-nowrap !t-h-auto !t-min-h-0 !t-rounded-b-none !t-rounded-t-lg !t-px-4 !t-py-2 focus:!t-ring-0 focus-visible:!t-ring-0 focus:!t-ring-offset-0";

const EntityTab = ({ entity, count, isActive, onSelect }: EntityTabProps) => {
  const handleSelect = () => onSelect(entity.id);

  const tabClassName = classNames(
    TAB_CLASS,
    isActive
      ? "!t-text-body-sm !t-bg-surface-purple !t-text-text-100 hover:!t-bg-surface-purple"
      : "!t-text-body-sm-lite !t-bg-surface-transparent !t-text-text-30"
  );

  const tab = (
    <Button
      customType="ghost"
      size="small"
      className={tabClassName}
      onClick={handleSelect}
    >
      {entity.name}
      <span className="t-ml-1.5 t-text-subtext-sm t-text-neutral-40">
        ({count})
      </span>
    </Button>
  );

  if (!isActive) {
    return tab;
  }

  return (
    <div className="t-flex t-shrink-0 t-items-end t-overflow-hidden t-rounded-t-xl t-border t-border-b-0 t-border-solid t-border-neutral-0">
      {tab}
    </div>
  );
};

const EntityTabs = ({
  entities,
  countById,
  selectedId,
  onSelect,
}: EntityTabsProps) => (
  <div className="hide-scrollbar t-flex t-shrink-0 t-items-end t-gap-1 t-overflow-x-auto t-pl-5 t-pr-6">
    {entities.map((entity) => (
      <EntityTab
        key={entity.id}
        entity={entity}
        count={countById[entity.id] ?? 0}
        isActive={entity.id === selectedId}
        onSelect={onSelect}
      />
    ))}
  </div>
);

const SKELETON_ROWS = [0, 1, 2, 3];

export const FilingsSkeleton = () => (
  <div className="t-flex t-flex-col">
    <Skeleton className="t-m-5 t-h-3 t-w-24" />

    {SKELETON_ROWS.map((row) => (
      <div
        key={row}
        className="t-flex t-items-center t-justify-between t-border-0 t-border-t t-border-solid t-border-neutral-0 t-px-5 t-py-5"
      >
        <div className="t-flex t-flex-col t-gap-2">
          <Skeleton className="t-h-4 t-w-48" />
          <Skeleton className="t-h-3 t-w-32" />
        </div>

        <Skeleton className="t-h-4 t-w-16" />
      </div>
    ))}
  </div>
);

const FilingsEmptyState = () => (
  <div className="t-flex t-min-h-[380px] t-flex-col t-items-center t-justify-center t-gap-3 t-px-6 t-py-16 t-text-center">
    <span className="t-text-neutral-40">
      <Wind />
    </span>

    <div className="t-flex t-flex-col t-items-center t-gap-2">
      <span className="t-text-subtext-sm t-text-text-30">
        No filings detected
      </span>
      <span className="t-text-body-sm-lite t-text-neutral-40">
        Start filling the details and we&apos;ll auto-detect the filings
        applicable to this entity
      </span>
    </div>
  </div>
);

type DetectedFilingsCardProps = {
  isSetupComplete: boolean;
  onCompleteSetup: () => void;
  entities: DashboardEntityRef[];

  countById: Record<string, number>;
  groupId: string;

  selectedEntityId: string;
  onSelectEntity: (id: string) => void;
  onPayAllFilings: (entityId: string) => void;
  onSingleFilingPaid: () => void;
};

export const DetectedFilingsCard = ({
  isSetupComplete,
  onCompleteSetup,
  entities,
  countById,
  groupId,
  selectedEntityId,
  onSelectEntity,
  onPayAllFilings,
  onSingleFilingPaid,
}: DetectedFilingsCardProps) => {
  const dispatch = useDispatch();
  const { alertToast } = useToast();
  const { isForeignCA } = useRoleBasedView();

  const [payFiling, setPayFiling] = useState<FilingNode | null>(null);

  const [singleInvoice, setSingleInvoice] = useState<BillingInvoice | null>(
    null
  );

  const payConfirmModal = useModal();
  const singleInvoiceModal = useModal();

  const [createProductInvoice, { isLoading: isCreatingInvoice }] =
    useAddProductsToCartAndCheckoutMutation();

  const handlePayFiling = useCallback(
    (filing: FilingNode) => {
      setPayFiling(filing);
      payConfirmModal.open();
    },
    [payConfirmModal]
  );

  const isMultiEntity = entities.length > 1;
  const selectedEntity =
    entities.find((entity) => entity.id === selectedEntityId) ?? entities[0];

  const {
    data: detectedFilings,
    isFetching,
    isLoading,
  } = useGetDetectedFilingsQuery(
    { groupId, entityId: selectedEntity.id },
    { skip: !groupId || !selectedEntity.id }
  );

  const seasonFilings = groupFilingsBySeason(detectedFilings);
  const isEmpty = !isFetching && seasonFilings.length === 0;

  const heading = isMultiEntity
    ? "Filings detected"
    : `Filings detected for ${entities[0].name}`;

  const closePayConfirm = () => payConfirmModal.close();

  const handlePaySingle = async (): Promise<void> => {
    if (!payFiling?.productId || !payFiling.productContentTypeId) return;

    try {
      const { invoice } = await createProductInvoice({
        groupId,
        entityId: selectedEntity.id,
        productData: [
          {
            entity_id: selectedEntity.id,
            product_id: payFiling.productId,
            product_content_type_id: payFiling.productContentTypeId,
            quantity: 1,
            payment_amount: payFiling.paymentAmount ?? 0,
            season: payFiling.season ?? null,
          },
        ],
        from_other: true,
      }).unwrap();

      setSingleInvoice(invoice);
      closePayConfirm();
      singleInvoiceModal.open();
    } catch (error) {
      const message =
        (error as BackendError)?.data?.error?.message ?? GENERIC_ERROR;
      alertToast({ message });
    }
  };

  const closeSingleInvoice = () => {
    setSingleInvoice(null);
    singleInvoiceModal.close();
  };

  const handleSinglePaid = () => {
    closeSingleInvoice();
    dispatch(emptyApi.util.invalidateTags([DETECTED_FILINGS_TAG]));
    onSingleFilingPaid();
  };

  const handlePayAll = () => {
    closePayConfirm();
    onPayAllFilings(selectedEntity.id);
  };

  const handleCompleteSetup = () => {
    closePayConfirm();
    onCompleteSetup();
  };

  return (
    <div className="t-flex t-flex-col t-gap-3">
      <div className="t-flex t-h-[500px] t-flex-col t-overflow-hidden t-rounded-xl t-border t-border-solid t-border-neutral-10 t-bg-white">
        <div className="t-flex t-items-center t-justify-between t-shrink-0 t-px-5 t-pb-3 t-pt-4">
          <span className="t-text-subtext t-text-text-60">{heading}</span>
          <ConditionalLink
            to={`/settings/services?entity=${selectedEntity.id}`}
          >
            <Button customType="ghost" size="small">
              <SmallPlusIcon className="t-mr-1" />
              Add filing
            </Button>
          </ConditionalLink>
        </div>

        {isMultiEntity && (
          <EntityTabs
            entities={entities}
            countById={countById}
            selectedId={selectedEntityId}
            onSelect={onSelectEntity}
          />
        )}

        <div className="t-min-h-0 t-flex-1 t-overflow-y-auto">
          {isLoading && <FilingsSkeleton />}

          {isEmpty && <FilingsEmptyState />}

          {!isLoading &&
            !isEmpty &&
            seasonFilings.map((season) => (
              <FilingsSeasonTable
                key={season.season}
                season={season.season}
                filings={season.filings}
                entityId={selectedEntity.id}
                onPay={handlePayFiling}
              />
            ))}
        </div>
      </div>
      {payConfirmModal.isOpen && payFiling && !isSetupComplete && (
        <PayFilingConfirmModal
          open
          onClose={closePayConfirm}
          title="Finish setup before paying?"
          description={
            <div className="t-flex t-flex-col t-gap-3">
              <p className="t-m-0">
                You've only completed part of your company profile. Completing
                onboarding may uncover additional required filings, so you can
                review and pay everything together.
              </p>
              <p className="t-m-0">
                You can still pay this overdue filing now if you prefer.
              </p>
            </div>
          }
          secondaryLabel="Complete setup"
          primaryLabel="Continue to payment"
          onSecondary={handleCompleteSetup}
          onPrimary={handlePaySingle}
          isPrimaryLoading={isCreatingInvoice}
        />
      )}
      {payConfirmModal.isOpen && payFiling && isSetupComplete && (
        <PayFilingConfirmModal
          open
          onClose={closePayConfirm}
          title="Pay all applicable filings instead?"
          description="We've identified all applicable filings for your company. You can continue paying just this overdue filing, or pay all detected filings together in a single checkout."
          secondaryLabel="Pay only this filing"
          primaryLabel="Pay all filings"
          onSecondary={handlePaySingle}
          onPrimary={handlePayAll}
          isSecondaryLoading={isCreatingInvoice}
        />
      )}
      {singleInvoiceModal.isOpen && singleInvoice && (
        <CheckoutModal
          type="invoices"
          open
          entityId={selectedEntity.id}
          invoices={[singleInvoice]}
          payerUserType={isForeignCA ? FCA_PAYER : CUSTOMER_PAYER}
          onInvoicePaid={handleSinglePaid}
          onClose={closeSingleInvoice}
        />
      )}
    </div>
  );
};
